import { useState, type ReactNode } from 'react';
import { cn } from '~/lib/cn';

/*
 * Interactive Zakat calculator.
 *
 * Everything below the inputs is derived during render (no setState-in-effect)
 * so the totals, Nisab comparison and Zakat-due amount always reflect the
 * current field values. Styling mirrors the send-money `CurrencyCalculator`
 * card (grey-25 rounded field blocks, thin `black-6` dividers, `text-h4` /
 * `text-body` type, `text-grey-500` / `text-ink` / `text-red-500`) so the card
 * sits consistently with the rest of the site.
 */

/** Estimated gold spot price (USD per gram). Surfaced in the footnote. */
const GOLD_PRICE_PER_GRAM = 88;
/** Nisab expressed in grams of gold (the classic 87.48g threshold). */
const NISAB_GOLD_GRAMS = 87.48;
/** Nisab value in USD = 87.48g × $88/g = $7,698.24. */
const NISAB = NISAB_GOLD_GRAMS * GOLD_PRICE_PER_GRAM;
/** Zakat is levied at 2.5% of net zakatable assets. */
const ZAKAT_RATE = 0.025;
/** One lunar (hijri) year ≈ 354 days. */
const LUNAR_YEAR_DAYS = 354;

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmt = (n: number) => usd.format(Number.isFinite(n) ? n : 0);

const dateFmt = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

/** Blank / malformed inputs count as 0 so totals never go NaN. */
function num(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Keep inputs numeric: digits + a single decimal point. */
function sanitize(value: string): string {
  const cleaned = value.replace(/[^\d.]/g, '');
  const [intPart, ...rest] = cleaned.split('.');
  return rest.length ? `${intPart}.${rest.join('')}` : intPart;
}

/**
 * Shared source of truth for the hero copy: the date exactly one lunar year ago
 * and the current Nisab threshold. Lives here so the page hero and the
 * calculator derive the same values from the same constants (no divergent logic).
 */
export function getZakatHeroInfo() {
  const heldSince = new Date(Date.now() - LUNAR_YEAR_DAYS * 24 * 60 * 60 * 1000);
  return {
    heldSinceLabel: dateFmt.format(heldSince),
    nisabLabel: fmt(NISAB),
    lunarYearDays: LUNAR_YEAR_DAYS,
  };
}

const inputClass =
  'w-auto max-w-40 min-w-4 field-sizing-content bg-transparent py-0 text-right text-body font-semibold outline-none tabular-nums placeholder:text-grey-500 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

/** One labelled money input row (label left, "$" prefix + right-aligned value). */
function MoneyRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const isZero = num(value) === 0;
  return (
    <label className="flex items-center justify-between gap-4 py-5">
      <span className="min-w-0 text-body leading-snug text-grey-900">{label}</span>
      <span className="flex shrink-0 items-center gap-1">
        <span className="text-body font-semibold text-ink">$</span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(sanitize(e.target.value))}
          placeholder="0"
          className={cn(inputClass, isZero ? 'text-grey-500' : 'text-ink')}
        />
      </span>
    </label>
  );
}

/** A grey-25 rounded field block (matches the calculator card's field group). */
function FieldBlock({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-black-6 rounded-[20px] bg-grey-25 px-5">{children}</div>;
}

/** A totals row (label left, value right). */
function TotalRow({
  label,
  value,
  bold,
  strong,
}: {
  label: string;
  value: string;
  bold?: boolean;
  /** The primary output row (Total Zakat) — a touch larger + bolder than `bold`. */
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={cn(
          'text-grey-900',
          strong ? 'text-h5 font-semibold' : cn('text-body', bold && 'font-semibold'),
        )}>
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums',
          strong
            ? 'text-h5 font-semibold text-ink'
            : cn('text-body', bold ? 'font-semibold text-ink' : 'font-medium text-ink'),
        )}>
        {value}
      </span>
    </div>
  );
}

const ZERO = '0';

export function ZakatCalculator() {
  const [gold, setGold] = useState('100');
  const [cashBank, setCashBank] = useState(ZERO);
  const [otherSavings, setOtherSavings] = useState(ZERO);
  const [stocksBusiness, setStocksBusiness] = useState(ZERO);
  const [moneyOwed, setMoneyOwed] = useState(ZERO);
  const [otherOutgoings, setOtherOutgoings] = useState(ZERO);

  // --- Derived totals (render-time, never stored in state) ------------------
  const totalAssets = num(gold) + num(cashBank) + num(otherSavings) + num(stocksBusiness);
  const liabilities = num(moneyOwed) + num(otherOutgoings);
  const netAssets = totalAssets - liabilities;
  const meetsNisab = netAssets >= NISAB;
  const zakatDue = meetsNisab ? netAssets * ZAKAT_RATE : 0;

  // Same source of truth as the summary for the due-since date + Nisab.
  const { heldSinceLabel, nisabLabel, lunarYearDays } = getZakatHeroInfo();

  function reset() {
    setGold(ZERO);
    setCashBank(ZERO);
    setOtherSavings(ZERO);
    setStocksBusiness(ZERO);
    setMoneyOwed(ZERO);
    setOtherOutgoings(ZERO);
  }

  return (
    <div className="mx-auto w-full max-w-[520px] rounded-[32px] bg-white p-6 md:p-8">
      {/* Header */}
      <div className="flex flex-col gap-7">
        <h2 className="text-h4 font-semibold text-ink">Zakat Calculator</h2>
        <p className="text-body leading-relaxed text-grey-500">
          {`Zakat is due on assets held since ${heldSinceLabel} — one lunar year (${lunarYearDays} days) ago. The current Nisab threshold is ${nisabLabel}.`}
        </p>
      </div>

      {/* Asset + liability inputs */}
      <div className="mt-12 flex flex-col gap-4">
        <FieldBlock>
          <MoneyRow label="Total value of gold & silver" value={gold} onChange={setGold} />
        </FieldBlock>

        <FieldBlock>
          <MoneyRow label="Cash & bank accounts" value={cashBank} onChange={setCashBank} />
          <MoneyRow label="Other savings" value={otherSavings} onChange={setOtherSavings} />
        </FieldBlock>

        <FieldBlock>
          <MoneyRow
            label="Stocks, shares & business assets"
            value={stocksBusiness}
            onChange={setStocksBusiness}
          />
        </FieldBlock>

        <FieldBlock>
          <MoneyRow label="Money you owe" value={moneyOwed} onChange={setMoneyOwed} />
          <MoneyRow
            label="Other outgoings due"
            value={otherOutgoings}
            onChange={setOtherOutgoings}
          />
        </FieldBlock>
      </div>

      {/* Totals — the "Total Zakat" row is the primary output. */}
      <div className="mt-10 h-px bg-black-6" />
      <div className="mt-8 flex flex-col gap-6">
        <TotalRow label="Total assets" value={fmt(totalAssets)} />
        <TotalRow
          label="Liabilities"
          value={liabilities > 0 ? `−${fmt(liabilities)}` : fmt(liabilities)}
        />
        <div className="h-px bg-black-6" />
        <TotalRow label="Net assets" value={fmt(netAssets)} />
        <TotalRow
          label="Nisab threshold"
          value={`${fmt(Math.abs(NISAB))} (${NISAB_GOLD_GRAMS}g gold)`}
        />
        <div className="h-px bg-black-6" />
        <TotalRow label="Total Zakat" value={fmt(zakatDue)} strong />
      </div>

      {/* Reset */}
      <div className="mt-14 flex justify-center">
        <button
          type="button"
          onClick={reset}
          className="cursor-pointer rounded-pill bg-grey-50 px-6 py-3 text-body font-medium text-grey-900 transition-colors hover:bg-grey-100">
          Reset
        </button>
      </div>

      {/* Footnote */}
      <div className="mt-14 flex flex-col gap-1.5 text-center text-body-s text-grey-500">
        <p>Gold price: {fmt(GOLD_PRICE_PER_GRAM)}/g (estimated)</p>
        <p>Consult a qualified scholar for your specific situation.</p>
      </div>
    </div>
  );
}
